import { createRoot } from 'react-dom/client';
import { App } from './App';
import { createWidgetApiClient } from './api';
import styles from './styles.css';

const currentScript = document.currentScript as HTMLScriptElement | null;
const widgetId = currentScript?.dataset.widgetId;
const scriptSrc = currentScript?.src;

if (!widgetId) {
  console.warn('[Audientic] widget script loaded without data-widget-id attribute');
} else if (!scriptSrc) {
  console.warn('[Audientic] widget script could not determine its own origin');
} else {
  mountWidget(widgetId, new URL(scriptSrc).origin, buildPageContext);
}

function buildPageContext(): Record<string, unknown> {
  const context: Record<string, unknown> = {
    page_url: window.location.href,
    page_title: document.title,
    referrer: document.referrer || null,
  };

  // Best available proxy for "local time at the dealership" — there's no
  // dealership timezone stored server-side (#194). Universal (not
  // adapter-specific) since every tenant benefits regardless of which
  // page-context adapter, if any, is active.
  try {
    context.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (err) {
    console.warn('[Audientic] could not resolve browser timezone', err);
  }

  const adapter = window.AudienticContextAdapter;
  if (typeof adapter === 'function') {
    try {
      const adapterContext = adapter();
      if (adapterContext && typeof adapterContext === 'object') {
        Object.assign(context, adapterContext);
      }
    } catch (err) {
      console.warn('[Audientic] context adapter threw', err);
    }
  }

  // Explicit AudienticContext always wins on conflict
  const explicit = window.AudienticContext;
  if (explicit && typeof explicit === 'object') {
    Object.assign(context, explicit);
  }

  return context;
}

function mountWidget(id: string, origin: string, getPageContext: () => Record<string, unknown>): void {
  const host = document.createElement('div');
  host.id = 'audientic-widget-host';
  document.body.appendChild(host);

  const shadow = host.attachShadow({ mode: 'open' });

  const styleEl = document.createElement('style');
  styleEl.textContent = styles;
  shadow.appendChild(styleEl);

  const mountPoint = document.createElement('div');
  shadow.appendChild(mountPoint);

  const api = createWidgetApiClient(origin, id);

  void api
    .recordEvent({
      eventType: 'widget_loaded',
      properties: {
        page_url: window.location.href,
        referrer: document.referrer || null,
      },
    })
    .catch((err) => console.warn('[Audientic] widget_loaded event failed', err));

  const root = createRoot(mountPoint);
  root.render(<App api={api} publicId={id} getPageContext={getPageContext} />);
}
